import { useEffect, useState } from 'react';
import { api } from '../api';
import type { ActiveElement, Chart, ChartEvent } from 'chart.js';
import { baseOptions, useChart } from './charts';
import type { Ctx } from './Dashboard';
import { CostRow, range, revenue } from './data';
import { ax, lastDay, ml, pct, rub, rubx, sum } from './fmt';

interface Detail { categories: { name: string; amount: number }[]; purposes: { name: string; amount: number }[] }

/** Затраты: тепловая карта «группа × месяц», структура за период, разбор выбранной группы. */
export function Costs({ ctx }: { ctx: Ctx }) {
  const { series: s, from, to, drill } = ctx;
  const M = s.months;
  const I = range(from, to);
  const rv = sum(I.map((i) => revenue(s, i)));
  const [mode, setMode] = useState<'rub' | 'pct'>('rub');
  const pctMode = mode === 'pct';
  const tot = s.costs
    .map((c) => [c, sum(I.map((i) => c.values[i]))] as [CostRow, number])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const [sel, setSel] = useState<string | null>(null);
  const cur = tot.find(([c]) => c.key === sel)?.[0] ?? tot[0]?.[0] ?? null;
  const cell = (c: CostRow, i: number) => (pctMode ? c.values[i] / (revenue(s, i) || 1) : c.values[i]);
  const mx = Math.max(1e-9, ...tot.flatMap(([c]) => I.map((i) => cell(c, i))));
  const period = { from: M[from], to: M[to] };

  const structure = useChart((T) => ({
    type: 'bar',
    data: {
      labels: tot.map(([c]) => (c.name.length > 26 ? `${c.name.slice(0, 25)}…` : c.name)),
      datasets: [{
        label: 'Сумма',
        data: tot.map(([, v]) => (pctMode ? v / (rv || 1) : v)),
        backgroundColor: tot.map(([c]) => (c.key === cur?.key ? T.ink : c.special ? T.berry : T.accent)),
        borderRadius: 4, maxBarThickness: 18,
        fmt: (v: number) => (pctMode ? pct(v) : `${rub(v)} · ${pct(v / (rv || 1))}`),
      }],
    },
    options: {
      ...baseOptions(T, { yfmt: (v) => String(v) }),
      indexAxis: 'y',
      onClick: (_e: ChartEvent, el: ActiveElement[]) => {
        if (el.length) setSel(tot[el[0].index][0].key);
      },
      scales: {
        x: { grid: { color: T.line }, border: { display: false }, ticks: { color: T.muted, callback: (v: number | string) => (pctMode ? `${Math.round(+v * 100)}%` : ax(+v)) } },
        y: { grid: { display: false }, ticks: { color: T.muted, autoSkip: false } },
      },
    },
  }) as never, [from, to, mode, cur?.key, s]);

  if (!tot.length) return <div className="card">За период расходов нет.</div>;

  return (
    <section className="grid">
      <div className="card s12">
        <div className="hd">
          <div>
            <h3>Тепловая карта затрат</h3>
            <p className="sub">Группа × месяц. Нажмите на ячейку, чтобы открыть операции</p>
          </div>
          <div className="seg" role="group" aria-label="Единицы">
            <button type="button" className={pctMode ? '' : 'on'} onClick={() => setMode('rub')}>₽</button>
            <button type="button" className={pctMode ? 'on' : ''} onClick={() => setMode('pct')}>% выручки</button>
          </div>
        </div>
        <div className="hm">
          <table>
            <thead>
              <tr>
                <th>Группа</th>
                {I.map((i) => <th key={i} className="m">{ml(M[i])}</th>)}
                <th className="m">Итого</th>
              </tr>
            </thead>
            <tbody>
              {tot.map(([c, total]) => (
                <tr key={c.key}>
                  <th>{c.name}</th>
                  {I.map((i) => {
                    const v = c.values[i];
                    const x = cell(c, i);
                    const k = Math.min(1, x / mx);
                    return (
                      <td
                        key={i}
                        title={`${c.name}, ${ml(M[i])}: ${rubx(v)} · ${pct(v / (revenue(s, i) || 1))} выручки`}
                        style={{
                          background: `color-mix(in srgb, var(${c.special ? '--berry' : '--accent'}) ${Math.round(k * 85)}%, var(--surface-2))`,
                          color: k > 0.5 ? 'var(--surface)' : 'var(--ink)',
                        }}
                        onClick={() => drill({ ...c.drill, from: M[i], to: M[i] }, 'costs')}
                      >
                        {v ? (pctMode ? pct(x, 1) : ax(v)) : '·'}
                      </td>
                    );
                  })}
                  <td style={{ background: 'var(--surface-2)' }} onClick={() => drill({ ...c.drill, ...period }, 'costs')}>
                    {pctMode ? pct(total / (rv || 1)) : ax(total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          Розовые строки — выплаты собственнику и CAPEX: их можно не считать расходом переключателями вверху.
        </p>
      </div>
      <div className="card s5">
        <h3>Структура за период</h3>
        <p className="sub">Нажмите на группу для разбора</p>
        <div className="ch" style={{ height: tot.length * 28 + 30 }}>
          <canvas ref={structure} role="img" aria-label="Расходы по группам за период" />
        </div>
      </div>
      {cur && <GroupCard ctx={ctx} c={cur} key={cur.key} />}
    </section>
  );
}

function GroupCard({ ctx, c }: { ctx: Ctx; c: CostRow }) {
  const { series: s, from, to, le, drill } = ctx;
  const M = s.months;
  const I = range(from, to);
  const rv = sum(I.map((i) => revenue(s, i)));
  const gv = I.map((i) => c.values[i]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const isGroup = !c.key.startsWith('_');
  useEffect(() => {
    if (!isGroup) return setDetail(null);
    const q = new URLSearchParams({ from: `${M[from]}-01`, to: lastDay(M[to]), group: c.key });
    if (le) q.set('le', le);
    api.get<Detail>(`/dashboard/group?${q}`).then(setDetail, () => setDetail(null));
  }, [c.key, M, from, to, le, isGroup]);

  const chart = useChart((T) => ({
    type: 'bar',
    data: {
      labels: I.map((i) => ml(M[i])),
      datasets: [{
        label: c.name, data: gv, backgroundColor: c.special ? T.berry : T.accent, borderRadius: 4, maxBarThickness: 24,
        fmt: (v: number, k: number) => `${rub(v)} · ${pct(v / (revenue(s, I[k]) || 1))} выручки`,
      }],
    },
    options: {
      ...baseOptions(T, { autoSkip: true }),
      onClick: (_e: ChartEvent, el: ActiveElement[]) => {
        if (el.length) {
          const m = M[I[el[0].index]];
          drill({ ...c.drill, from: m, to: m }, 'costs');
        }
      },
    },
  }) as never, [c.key, from, to, s]);

  return (
    <div className="card s7">
      <div className="hd">
        <div>
          <h3>{c.name}</h3>
          <p className="sub">
            {rub(sum(gv))} за период · {pct(sum(gv) / (rv || 1))} выручки · {rub(sum(gv) / I.length)} в месяц
          </p>
        </div>
        <button className="btn" type="button" onClick={() => drill({ ...c.drill, from: M[from], to: M[to] }, 'costs')}>
          Все операции →
        </button>
      </div>
      <div className="ch" style={{ height: 190 }}>
        <canvas ref={chart} role="img" aria-label={`Динамика: ${c.name}`} />
      </div>
      {detail && (
        <div className="grid" style={{ marginTop: 10 }}>
          <div className="s7 tbl">
            <table>
              <thead>
                <tr><th>Крупнейшие назначения</th><th className="n">Сумма</th></tr>
              </thead>
              <tbody>
                {detail.purposes.map((x) => (
                  <tr key={x.name}><td>{x.name}</td><td className="n">{rubx(x.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="s5 tbl">
            <table>
              <thead>
                <tr><th>Статьи внутри</th><th className="n">Сумма</th></tr>
              </thead>
              <tbody>
                {detail.categories.map((x) => (
                  <tr key={x.name}><td>{x.name}</td><td className="n">{rubx(x.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
